(() => {
  const debounce = (callback, delay = 250) => {
    let timeout;
    return (...args) => {
      window.clearTimeout(timeout);
      timeout = window.setTimeout(() => callback(...args), delay);
    };
  };

  const closeDropdowns = (except) => {
    document.querySelectorAll('[data-nav-item]').forEach((item) => {
      if (item === except) return;
      item.querySelector('[data-dropdown]')?.classList.remove('is-open');
      item.querySelector('[data-dropdown-toggle]')?.setAttribute('aria-expanded', 'false');
    });
  };

  document.querySelectorAll('[data-nav-item]').forEach((item) => {
    const toggle = item.querySelector('[data-dropdown-toggle]');
    const dropdown = item.querySelector('[data-dropdown]');
    if (!toggle || !dropdown) return;
    let closeTimer;

    const open = () => {
      window.clearTimeout(closeTimer);
      closeDropdowns(item);
      dropdown.classList.add('is-open');
      toggle.setAttribute('aria-expanded', 'true');
    };
    const close = () => {
      window.clearTimeout(closeTimer);
      dropdown.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
    };
    const scheduleClose = () => {
      window.clearTimeout(closeTimer);
      closeTimer = window.setTimeout(close, 180);
    };

    toggle.addEventListener('click', () => {
      if (toggle.getAttribute('aria-expanded') === 'true') close();
      else open();
    });
    item.addEventListener('mouseenter', open);
    item.addEventListener('mouseleave', scheduleClose);
    item.addEventListener('focusout', (event) => {
      if (!item.contains(event.relatedTarget)) scheduleClose();
    });
    item.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        close();
        toggle.focus();
      }
    });
  });

  document.addEventListener('click', (event) => {
    if (!event.target.closest('[data-nav-item]')) closeDropdowns();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeDropdowns();
  });

  document.addEventListener('change', (event) => {
    if (event.target.matches('[data-localization-select]')) event.target.form?.submit();
  });

  document.querySelectorAll('[data-predictive-search]').forEach((container) => {
    const input = container.querySelector('[data-predictive-search-input]');
    const panel = container.querySelector('[data-predictive-search-panel]');
    if (!input || !panel) return;

    const search = debounce(async () => {
      const term = input.value.trim();
      if (term.length < 2) {
        panel.hidden = true;
        input.setAttribute('aria-expanded', 'false');
        return;
      }

      try {
        const root = window.Shopify?.routes?.root || '/';
        const url = `${root}search/suggest?q=${encodeURIComponent(term)}&resources[type]=product,collection,query&resources[limit]=4&section_id=orvixio-predictive-search`;
        const response = await fetch(url);
        if (!response.ok) throw new Error('Predictive search request failed');
        const markup = await response.text();
        const documentFragment = new DOMParser().parseFromString(markup, 'text/html');
        const results = documentFragment.querySelector('[data-predictive-search-results]');
        if (!results) {
          panel.hidden = true;
          input.setAttribute('aria-expanded', 'false');
          return;
        }
        panel.innerHTML = results.innerHTML;
        panel.hidden = false;
        input.setAttribute('aria-expanded', 'true');
      } catch {
        panel.hidden = true;
        input.setAttribute('aria-expanded', 'false');
      }
    });

    input.addEventListener('input', search);
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        panel.hidden = true;
        input.setAttribute('aria-expanded', 'false');
      }
    });
  });

  const menu = document.querySelector('[data-mobile-menu]');
  const menuOpen = document.querySelector('[data-mobile-menu-open]');
  const menuClose = document.querySelector('[data-mobile-menu-close]');
  const setMenu = (open) => {
    if (!menu || !menuOpen) return;
    menu.classList.toggle('is-open', open);
    menu.setAttribute('aria-hidden', String(!open));
    menuOpen.setAttribute('aria-expanded', String(open));
    document.body.style.overflow = open ? 'hidden' : '';
  };
  menuOpen?.addEventListener('click', () => setMenu(true));
  menuClose?.addEventListener('click', () => setMenu(false));
  menu?.querySelectorAll('a').forEach((link) => link.addEventListener('click', () => setMenu(false)));
  menu?.querySelectorAll('[data-mobile-nav-toggle]').forEach((button) => {
    const panel = document.getElementById(button.getAttribute('aria-controls'));
    if (!panel) return;
    button.addEventListener('click', () => {
      const expanded = button.getAttribute('aria-expanded') === 'true';
      button.setAttribute('aria-expanded', String(!expanded));
      panel.hidden = expanded;
    });
  });

  const mobileSearchToggle = document.querySelector('[data-mobile-search-toggle]');
  const mobileSearchRow = document.querySelector('[data-mobile-search-row]');
  mobileSearchToggle?.addEventListener('click', () => {
    const open = !mobileSearchRow.classList.contains('is-open');
    mobileSearchRow.classList.toggle('is-open', open);
    mobileSearchToggle.setAttribute('aria-expanded', String(open));
    if (open) mobileSearchRow.querySelector('input')?.focus();
  });

  const initInteractiveHero = (root = document) => root.querySelectorAll('[data-interactive-hero]').forEach((hero) => {
    if (hero.dataset.heroReady === 'true') return;
    hero.dataset.heroReady = 'true';
    const cards = [...hero.querySelectorAll('[data-hero-card]')];
    const controls = [...hero.querySelectorAll('[data-hero-control]')];
    const scenes = [...hero.querySelectorAll('[data-hero-scene]')];
    const details = [...hero.querySelectorAll('[data-hero-detail]')];
    const progress = hero.querySelector('[data-hero-progress]');
    const progressGroup = progress?.closest('.spotlight-cards__progress');
    const lens = hero.querySelector('[data-hero-lens]');
    let lensFrame = 0;
    const syncHeroLens = () => {
      if (!(lens instanceof HTMLElement)) return;
      cancelAnimationFrame(lensFrame);
      lensFrame = requestAnimationFrame(() => {
        const activeCard = cards.find((card) => card.classList.contains('is-active'));
        if (!(activeCard instanceof HTMLElement)) return;
        lens.style.setProperty('--orvixio-hero-lens-y', `${activeCard.offsetTop}px`);
        lens.style.setProperty('--orvixio-hero-lens-height', `${activeCard.offsetHeight}px`);
        lens.classList.add('is-ready');
      });
    };
    const activate = (index) => {
      cards.forEach((card, cardIndex) => {
        const active = cardIndex === index;
        card.classList.toggle('is-active', active);
        card.querySelector('.spotlight-card__selector')?.setAttribute('aria-pressed', String(active));
      });
      scenes.forEach((scene, sceneIndex) => {
        const active = sceneIndex === index;
        scene.classList.toggle('is-active', active);
        scene.setAttribute('aria-hidden', String(!active));
      });
      controls.forEach((control) => {
        const active = Number(control.dataset.heroControl) === index;
        if (control.getAttribute('role') === 'tab') control.setAttribute('aria-selected', String(active));
        if (control.classList.contains('spotlight-card__selector')) control.setAttribute('aria-pressed', String(active));
      });
      details.forEach((detail) => detail.classList.toggle('is-hidden', Number(detail.dataset.heroDetail) !== index));
      if (progress) progress.textContent = String(index + 1).padStart(2, '0');
      if (progressGroup) progressGroup.setAttribute('aria-label', `Featured product ${index + 1} of ${cards.length}`);
      syncHeroLens();
    };

    cards.forEach((card, index) => {
      card.addEventListener('mouseenter', () => activate(index));
      card.addEventListener('focusin', () => activate(index));
    });
    controls.forEach((control) => control.addEventListener('click', () => activate(Number(control.dataset.heroControl))));
    const heroResizeObserver = new ResizeObserver(syncHeroLens);
    cards.forEach((card) => heroResizeObserver.observe(card));

    const mobileCarousel = hero.querySelector('[data-mobile-spotlight]');
    const mobileTrack = mobileCarousel?.querySelector('[data-mobile-spotlight-track]');
    const mobileSlides = [...(mobileCarousel?.querySelectorAll('[data-mobile-spotlight-slide]') || [])];
    const mobileIndexLabel = mobileCarousel?.querySelector('[data-mobile-spotlight-index]');
    const mobilePrevious = mobileCarousel?.querySelector('[data-mobile-spotlight-prev]');
    const mobileNext = mobileCarousel?.querySelector('[data-mobile-spotlight-next]');
    const mobileControls = [...(mobileCarousel?.querySelectorAll('[data-mobile-spotlight-control]') || [])];
    const mobileLens = mobileCarousel?.querySelector('[data-mobile-spotlight-lens]');
    const mobileStatus = mobileCarousel?.querySelector('[data-mobile-spotlight-status]');
    const mobilePreviousCue = mobileCarousel?.querySelector('[data-mobile-spotlight-cue-previous]');
    const mobileNextCue = mobileCarousel?.querySelector('[data-mobile-spotlight-cue-next]');
    let mobileIndex = 0;
    let mobileTouchStartX = null;
    const setMobileIndex = (index) => {
      if (!mobileSlides.length) return;
      mobileIndex = Math.max(0, Math.min(mobileSlides.length - 1, index));
      mobileCarousel.dataset.activeIndex = String(mobileIndex);
      mobileSlides.forEach((slide, slideIndex) => {
        const active = slideIndex === mobileIndex;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
      });
      if (mobileIndexLabel) mobileIndexLabel.textContent = String(mobileIndex + 1);
      if (mobilePrevious) mobilePrevious.disabled = mobileIndex === 0;
      if (mobileNext) mobileNext.disabled = mobileIndex === mobileSlides.length - 1;
      if (mobilePreviousCue) {
        mobilePreviousCue.hidden = mobileIndex === 0;
        mobilePreviousCue.disabled = mobileIndex === 0;
      }
      if (mobileNextCue) {
        mobileNextCue.hidden = mobileIndex === mobileSlides.length - 1;
        mobileNextCue.disabled = mobileIndex === mobileSlides.length - 1;
      }
      mobileControls.forEach((control, controlIndex) => {
        control.setAttribute('aria-pressed', String(controlIndex === mobileIndex));
      });
      if (mobileLens instanceof HTMLElement) {
        mobileLens.parentElement?.style.setProperty('--orvixio-mobile-lens-x', `${mobileIndex * 52}px`);
      }
      if (mobileStatus) {
        mobileStatus.textContent = `Featured product ${mobileIndex + 1} of ${mobileSlides.length}`;
      }
    };
    mobilePrevious?.addEventListener('click', () => setMobileIndex(mobileIndex - 1));
    mobileNext?.addEventListener('click', () => setMobileIndex(mobileIndex + 1));
    mobileControls.forEach((control) => {
      control.addEventListener('click', () => setMobileIndex(Number(control.dataset.mobileSpotlightControl)));
    });
    mobilePreviousCue?.addEventListener('click', () => setMobileIndex(mobileIndex - 1));
    mobileNextCue?.addEventListener('click', () => setMobileIndex(mobileIndex + 1));
    mobileTrack?.addEventListener('touchstart', (event) => {
      mobileTouchStartX = event.touches[0]?.clientX ?? null;
    }, { passive: true });
    mobileTrack?.addEventListener('touchend', (event) => {
      const mobileTouchEndX = event.changedTouches[0]?.clientX;
      if (mobileTouchStartX == null || mobileTouchEndX == null) return;
      const delta = mobileTouchEndX - mobileTouchStartX;
      mobileTouchStartX = null;
      if (Math.abs(delta) < 40) return;
      setMobileIndex(mobileIndex + (delta < 0 ? 1 : -1));
    }, { passive: true });
    activate(0);
    setMobileIndex(0);
  });
  initInteractiveHero();
  document.addEventListener('shopify:section:load', (event) => initInteractiveHero(event.target));

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      closeDropdowns();
      setMenu(false);
    }
  });
})();



